import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { parse } from 'vscode/vscode/vs/base/common/json';
import { setProperty } from 'vscode/vscode/vs/base/common/jsonEdit';
import { Queue } from 'vscode/vscode/vs/base/common/async';
import { EditOperation } from 'vscode/vscode/vs/editor/common/core/editOperation';
import { Range } from 'vscode/vscode/vs/editor/common/core/range';
import { Selection } from 'vscode/vscode/vs/editor/common/core/selection';
import { ITextFileService } from 'vscode/vscode/vs/workbench/services/textfile/common/textfiles.service';
import { IFileService } from 'vscode/vscode/vs/platform/files/common/files.service';
import { ITextModelService } from 'vscode/vscode/vs/editor/common/services/resolverService';
import { JSONEditingError } from './jsonEditing.js';
import 'vscode/vscode/vs/platform/instantiation/common/instantiation';

const _moduleId = "vs/workbench/services/configuration/common/jsonEditingService";
let JSONEditingService = class JSONEditingService {
    constructor(fileService, textModelResolverService, textFileService) {
        this.fileService = fileService;
        this.textModelResolverService = textModelResolverService;
        this.textFileService = textFileService;
        this.queue = ( (new Queue()));
    }
    write(resource, values) {
        return Promise.resolve(this.queue.queue(() => this.doWriteConfiguration(resource, values)));
    }
    async doWriteConfiguration(resource, values) {
        const reference = await this.resolveAndValidate(resource, true);
        try {
            await this.writeToBuffer(reference.object.textEditorModel, values);
        }
        finally {
            reference.dispose();
        }
    }
    async writeToBuffer(model, values) {
        let hasEdits = false;
        for (const value of values) {
            const edit = this.getEdits(model, value)[0];
            hasEdits = !!edit && this.applyEditsToBuffer(edit, model);
        }
        if (hasEdits) {
            return this.textFileService.save(model.uri);
        }
    }
    applyEditsToBuffer(edit, model) {
        const startPosition = model.getPositionAt(edit.offset);
        const endPosition = model.getPositionAt(edit.offset + edit.length);
        const range = ( (new Range(
            startPosition.lineNumber,
            startPosition.column,
            endPosition.lineNumber,
            endPosition.column
        )));
        const currentText = model.getValueInRange(range);
        if (edit.content !== currentText) {
            const editOperation = currentText ? EditOperation.replace(range, edit.content) : EditOperation.insert(startPosition, edit.content);
            model.pushEditOperations([( (new Selection(
                startPosition.lineNumber,
                startPosition.column,
                startPosition.lineNumber,
                startPosition.column
            )))], [editOperation], () => []);
            return true;
        }
        return false;
    }
    getEdits(model, configurationValue) {
        const { tabSize, insertSpaces } = model.getOptions();
        const eol = model.getEOL();
        const { path, value } = configurationValue;
        if (!path.length) {
            const content = JSON.stringify(value, null, insertSpaces ? ' '.repeat(tabSize) : '\t');
            return [{
                    content,
                    length: content.length,
                    offset: 0
                }];
        }
        return setProperty(model.getValue(), path, value, { tabSize, insertSpaces, eol });
    }
    async resolveModelReference(resource) {
        const exists = await this.fileService.exists(resource);
        if (!exists) {
            await this.textFileService.write(resource, '{}', { encoding: 'utf8' });
        }
        return this.textModelResolverService.createModelReference(resource);
    }
    hasParseErrors(model) {
        const parseErrors = [];
        parse(model.getValue(), parseErrors, { allowTrailingComma: true, allowEmptyContent: true });
        return parseErrors.length > 0;
    }
    async resolveAndValidate(resource, checkDirty) {
        const reference = await this.resolveModelReference(resource);
        const model = reference.object.textEditorModel;
        if (this.hasParseErrors(model)) {
            reference.dispose();
            return this.reject(0 );
        }
        return reference;
    }
    reject(code) {
        const message = this.toErrorMessage(code);
        return Promise.reject(( (new JSONEditingError(message, code))));
    }
    toErrorMessage(error) {
        switch (error) {
            case 0 : {
                return ( localizeWithPath(
                    _moduleId,
                    0,
                    "Unable to write into the file. Please open the file to correct errors/warnings in the file and try again."
                ));
            }
        }
    }
};
JSONEditingService = ( (__decorate([
    ( (__param(0, IFileService))),
    ( (__param(1, ITextModelService))),
    ( (__param(2, ITextFileService)))
], JSONEditingService)));

export { JSONEditingService };
